import nuskin from './namespace.js'; // import global namespace we can attach to
import RunConfigService from './runConfigService';
import UrlService from './urlService';
import ConfigService from './configurationService';
import _ from 'lodash';

/**
 * Fetch the runtime configuration environment variable for:
 * LOCAL || DEV || TEST || STAGE || PROD
 *
 * This function ensures there is a valid runtime config env. If none
 * is detected return a null.
 *
 * @returns {array} || null
 */
function getRunConfigEnv(runConfigEnv) {
    let baseUrl;
    let fbBasebaseUrl = null;

    if (!runConfigEnv) {
        runConfigEnv = RunConfigService.getEnvironment();
    }

    // Ensure that the acquired run-config env matches one of the following
    // predefined environment modes, else just return null:
    switch (true) {
        case runConfigEnv === RunConfigService.LOCALHOST:
            baseUrl = `https://${RunConfigService.TEST}`;
            break;
        case runConfigEnv === RunConfigService.DEV:
            baseUrl = `https://${RunConfigService.DEV}`;
            break;
        case runConfigEnv === RunConfigService.TEST:
        case runConfigEnv.includes(RunConfigService.TESTMYNUSKIN):
            baseUrl = `https://${RunConfigService.TEST}`;
            break;
        case runConfigEnv.includes(RunConfigService.FEATUREBRANCH):
            baseUrl = `https://${RunConfigService.TEST}`;
            let port = window.location.port ? `:${window.location.port}` : '';
            fbBasebaseUrl = `${window.location.protocol}//${runConfigEnv}${port}`;
            break;
        case runConfigEnv === RunConfigService.STAGE:
            baseUrl = `https://${RunConfigService.STAGE}`;
            break;
        case runConfigEnv === RunConfigService.PROD:
        case runConfigEnv.includes(RunConfigService.PRODMYNUSKIN):
        case runConfigEnv.includes(RunConfigService.PRODPUBSVR):
        case runConfigEnv.includes(RunConfigService.PRODNGPILOT):
            baseUrl = `https://${RunConfigService.PROD}`;
            break;
        default:
            console.error('Unable to determine environment. Defaulting to test.nuskin.com');
            baseUrl = "https://" + RunConfigService.TEST;
            break;
    }

    return [baseUrl, fbBasebaseUrl];
}

/**
 * This function takes a configuration object with at least an env that conforms to
 * one of the following:
 *
 * dev || test || prod
 *
 * and sets the baseUrl according to the env variable.
 *
 * @param config
 * @return void if no config.env is detected
 */
function setConfigBaseUrl(config) {
    if (!config.env) {
        return;
    } else if (config.env === 'dev') {
        config.baseUrl = "https://dev.nuskin.com";
    } else if (config.env === 'test') {
        config.baseUrl = "https://test.nuskin.com";
    } else if (config.env === 'prod') {
        config.baseUrl = "https://www.nuskin.com";
    }
}

/**
 * Smart initialization function that detects if a the runtime env is overriden through the passed in
 * config object. If so then set the base url in the runtime config accordingly, else detect the runtime
 * config and set the base url off of that. If a base url is already set, do not override it.
 * This gives programmers the ability to override the runtime config env and base url as well as add
 * any additional configurations.
 *
 * @param config
 */
nuskin.init = (config = {}) => {
    // this is to make sure aem config is loaded for jsp's that still refer to aem.configuration.
    ConfigService.getMarketConfig();

    let [baseUrl, fbBaseUrl] = getRunConfigEnv(UrlService.extractDomain());

    if (config.env) {
        setConfigBaseUrl(config);
        delete(config.env);
    } else if (!config.baseUrl) {
        config.baseUrl = baseUrl;
    }

    if (fbBaseUrl) {
        config.fbBaseUrl = fbBaseUrl;
    }

    let runConfig = RunConfigService.getRunConfig();

    if (!config.language || !config.country) {
        const [language, country] = UrlService.getLocaleFromUrl();
        // use locale on URL, else use en_US
        config.language = language || 'en';
        config.country = country || 'US';
    }

    config.assessmentApp = config.assessmentApp || false;
    config.id = Math.floor(Math.random() * 1000000000000);

    const extendedRunConfig = _.extend(runConfig, config);

    nuskin.util.Initializer.init(extendedRunConfig);
};

export default nuskin.init;
